import { useMemo } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { hasPermission, PermInput } from './permissions';

export type CanCheck = (required: PermInput | PermInput[], mode?: 'any' | 'all') => boolean;

/**
 * Returns a `can(...)` checker bound to the current user's permissions.
 *
 * - `can(billing.read.GetBillingData)` — single permission
 * - `can([...], 'any')` (default) — at least one of the listed permissions
 * - `can([...], 'all')` — every listed permission
 *
 * Permissions are written via dot access (e.g. `brand.create.CreateBrand`) or
 * as plain slug strings. Wildcard grants (e.g. "campaign.*") are honored.
 */
export const useCan = (): CanCheck => {
  const permissions = useAuthStore((s) => s.permissions);

  return useMemo<CanCheck>(() => {
    return (required, mode = 'any') => {
      const list = Array.isArray(required) ? required : [required];
      if (list.length === 0) return true;
      return mode === 'all'
        ? list.every((p) => hasPermission(permissions, p))
        : list.some((p) => hasPermission(permissions, p));
    };
  }, [permissions]);
};

export default useCan;
